import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { organization } = useAuth();
  const [items, setItems] = useState([]);
  const [discountTotal, setDiscountTotal] = useState(0);

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + (item.unit_price * item.quantity - item.line_discount), 0);
  const taxableAmount = subtotal - discountTotal;
  
  // VAT calculation for VAT-registered
  const vatRate = organization?.vat_rate || 12;
  const vatAmount = organization?.vat_mode === 'vat_registered' 
    ? Math.round((taxableAmount - (taxableAmount / (1 + vatRate / 100))) * 100) / 100
    : 0;
  
  const grandTotal = taxableAmount;

  // Add item to cart
  const addItem = useCallback((product) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(i => i.product_id === product.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1,
        };
        return updated;
      }
      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        unit_price: product.price,
        quantity: 1,
        line_discount: 0,
        notes: null,
      }];
    });
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.product_id !== productId));
    } else {
      setItems(prev => prev.map(i => 
        i.product_id === productId ? { ...i, quantity } : i
      ));
    }
  }, []);

  // Update item discount
  const updateLineDiscount = useCallback((productId, discount) => {
    setItems(prev => prev.map(i => 
      i.product_id === productId ? { ...i, line_discount: discount } : i
    ));
  }, []);

  // Update item notes
  const updateNotes = useCallback((productId, notes) => {
    setItems(prev => prev.map(i => 
      i.product_id === productId ? { ...i, notes } : i
    ));
  }, []);

  // Remove item
  const removeItem = useCallback((productId) => {
    setItems(prev => prev.filter(i => i.product_id !== productId));
  }, []);

  // Clear cart
  const clearCart = useCallback(() => {
    setItems([]);
    setDiscountTotal(0);
  }, []);

  // Increment quantity
  const incrementQuantity = useCallback((productId) => {
    setItems(prev => prev.map(i => 
      i.product_id === productId ? { ...i, quantity: i.quantity + 1 } : i
    ));
  }, []);

  // Decrement quantity
  const decrementQuantity = useCallback((productId) => {
    setItems(prev => {
      const item = prev.find(i => i.product_id === productId);
      if (item && item.quantity <= 1) {
        return prev.filter(i => i.product_id !== productId);
      }
      return prev.map(i => 
        i.product_id === productId ? { ...i, quantity: i.quantity - 1 } : i
      );
    });
  }, []);

  const value = {
    items,
    subtotal,
    discountTotal,
    setDiscountTotal,
    vatAmount,
    grandTotal,
    vatRate,
    itemCount: items.reduce((sum, i) => sum + i.quantity, 0),
    addItem,
    updateQuantity,
    updateLineDiscount,
    updateNotes,
    removeItem,
    clearCart,
    incrementQuantity,
    decrementQuantity,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
